import useSWR from 'swr';
import apiClient from '../helpers/ApiClient';

const fetcher = async (url) => {
  const res = await apiClient.get(url);
  return res.data;
};

// ─── Modern Chart of Accounts ─────────────────────────
export function useChartOfAccountsTree() {
  const { data, error, isLoading, mutate } = useSWR('/accounting/chart-of-accounts', fetcher);
  return {
    data,
    tree: data?.tree || [],
    flatAccounts: data?.flatAccounts || [],
    groups: data?.groups || [],
    isLoading,
    isError: error,
    mutate
  };
}

export async function createAccountGroup(groupData) {
  const res = await apiClient.post('/accounting/groups', groupData);
  return res.data;
}

export async function createLedgerAccount(ledgerData) {
  const res = await apiClient.post('/accounting/ledgers', ledgerData);
  return res.data;
}

export async function updateLedgerAccount(id, ledgerData) {
  const res = await apiClient.put(`/accounting/ledgers/${id}`, ledgerData);
  return res.data;
}

// ─── Double-Entry Vouchers ───────────────────────────
export async function createAccountingVoucher(voucherData) {
  const res = await apiClient.post('/accounting/vouchers', voucherData);
  return res.data;
}

// ─── Kharid Khata (Purchase Book / Schedule 9) ───────
export function useKharidKhata(params = {}) {
  const query = new URLSearchParams();
  if (params.fiscalYear) query.set('fiscal_year', params.fiscalYear);
  if (params.nepaliMonth) query.set('nepali_month', params.nepaliMonth);
  if (params.supplierId) query.set('supplier_id', params.supplierId);
  if (params.fromDate) query.set('from_date', params.fromDate);
  if (params.toDate) query.set('to_date', params.toDate);

  const url = `/accounting/kharid-khata?${query.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);

  return {
    data,
    entries: data?.entries || [],
    summary: data?.summary || {},
    supplierSummary: data?.supplierSummary || [],
    isLoading,
    isError: error,
    mutate
  };
}

// ─── Bikri Khata (Sales Book / Schedule 8) ───────────
export function useBikriKhata(params = {}) {
  const query = new URLSearchParams();
  if (params.fiscalYear) query.set('fiscal_year', params.fiscalYear);
  if (params.nepaliMonth) query.set('nepali_month', params.nepaliMonth);
  if (params.customerId) query.set('customer_id', params.customerId);
  if (params.fromDate) query.set('from_date', params.fromDate);
  if (params.toDate) query.set('to_date', params.toDate);

  const url = `/accounting/bikri-khata?${query.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);

  return {
    data,
    entries: data?.entries || [],
    summary: data?.summary || {},
    customerSummary: data?.customerSummary || [],
    isLoading,
    isError: error,
    mutate
  };
}

// ─── Masikabari (Monthly VAT Return & Closing) ────────
export function useMasikabari(params = {}) {
  const query = new URLSearchParams();
  if (params.fiscalYear) query.set('fiscal_year', params.fiscalYear);
  if (params.nepaliMonth) query.set('nepali_month', params.nepaliMonth);

  const url = `/accounting/masikabari?${query.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);

  return {
    data,
    period: data?.period || {},
    salesSummary: data?.salesSummary || {},
    purchaseSummary: data?.purchaseSummary || {},
    vatSummary: data?.vatSummary || {},
    financialSummary: data?.financialSummary || {},
    isLoading,
    isError: error,
    mutate
  };
}

// ─── Accounting Period Lock ───────────────────────────
export function useAccountingPeriods(fiscalYear = null) {
  const url = fiscalYear ? `/accounting/periods?fiscal_year=${fiscalYear}` : '/accounting/periods';
  const { data, error, isLoading, mutate } = useSWR(url, fetcher);

  return {
    periods: data?.periods || [],
    isLoading,
    isError: error,
    mutate
  };
}

export async function closeAccountingPeriod(payload) {
  const res = await apiClient.post('/accounting/periods/close', payload);
  return res.data;
}

export async function reopenAccountingPeriod(payload) {
  const res = await apiClient.post('/accounting/periods/reopen', payload);
  return res.data;
}
